import React, { useCallback, useContext, useEffect, useLayoutEffect, useState } from 'react'
import { useLocation } from "react-router-dom";
import ListPagination from "components/ListPagination";
import { Row, Spinner } from "react-bootstrap";
import ImageGallery from "components/ImageGallery";
import CaseApi from "api/case-api";
import { CaseImagesContext } from "pages/cases/view/case-images/CaseImages";


const caseApi = new CaseApi()

const AllImages = () => {
    const location = useLocation();
    const [totalPages, setTotalPages] = useState(0);
    const [gallery, setGallery] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [imageSequenceNumber, setImageSequenceNumber] = useState<string>();
    const {
        captureDate,
        caseInfo,
        locationInfo,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        onGoToImage,
        holdPageFetchForGoToImage
    } = useContext(CaseImagesContext)

    // On (re)mount: honor an explicit page size from the URL, otherwise PRESERVE whatever page/size is
    // already in context. CaseImages does NOT remount on in-app view switches (the view is a route param),
    // so currentPage/pageSize survive a Select -> All switch — resetting them here would bounce you back to
    // page 1. A genuinely fresh load already starts at page 1 / the default via CaseImages' own initial state.
    // useLayoutEffect so any size change lands before the first fetch (avoids a double fetch).
    useLayoutEffect(() => {
        const requestedPageSize = new URLSearchParams(location.search).get('pageSize')?.trim();
        if (requestedPageSize) {
            setPageSize(parseInt(requestedPageSize, 10) || 240)
        }
    }, [])

    // Fetch all images
    useEffect(() => {
        // A go-to-image boot resolves its real page first — don't burn a fetch (and a page
        // of image downloads) on page 1 that the jump is about to replace anyway
        if (holdPageFetchForGoToImage) {
            return;
        }
        const getImages = async () => {
            try {
                setIsLoading(true);
                const offset = (currentPage - 1) * pageSize;
                const {images, count} = await caseApi.getLocationImages(
                    caseInfo.id, locationInfo.id, {
                        capture_date: captureDate,
                        limit: pageSize,
                        offset: offset,
                        image_sequence: imageSequenceNumber
                    });
                setGallery([...images]);
                setTotalPages(Math.ceil(count / pageSize));
            } catch (e) {
                console.error(e)
            } finally {
                setIsLoading(false);
            }
        }
        getImages();
    }, [currentPage, pageSize, captureDate, caseInfo.id, locationInfo.id, imageSequenceNumber, holdPageFetchForGoToImage]);

    const onSetPageSize = useCallback((size: number) => {
        setPageSize(size);
    }, []);

    const onChangePage = useCallback((page: number) => {
        setCurrentPage(page);
    }, []);


    return (
        <React.Fragment>

            <ListPagination
                className="pagination-top"
                pageSize={pageSize}
                currentPage={currentPage}
                totalPages={totalPages}
                onSetPageSize={onSetPageSize}
                onChangePage={onChangePage}
                onGoToImage={onGoToImage}
            />

            <Row className="filterable-content position-relative">
                {isLoading &&
                    <div className="d-flex justify-content-center">
                        <Spinner className="text-primary m-2"/>
                    </div>
                }
                {
                    !isLoading && gallery.length === 0 &&
                    <span className="text-muted">No images found</span>
                }
                <ImageGallery
                    name="All"
                    images={gallery}/>
            </Row>

            <ListPagination
                className="pagination-bottom"
                pageSize={pageSize}
                currentPage={currentPage}
                totalPages={totalPages}
                onSetPageSize={onSetPageSize}
                onChangePage={onChangePage}
                onGoToImage={onGoToImage}
            />
        </React.Fragment>
    );
}

export default React.memo(AllImages);